/**
 * FontManager.ts;
 * Gestionnaire des polices et du chargement des fonts;
 */
import type Konva from 'konva';
import { logger } from './utils/ProfessionalLogger';
import { APP_CONFIG } from './config/app.config';
export interface FontInfo {
  family: string;
  category: string;
  variants: string[];
  loaded: boolean;
  source: 'local' | 'google' | 'system';
}
export interface FontCategory {
  name: string;
  fonts: string[];
}
export class FontManager {
  private static instance: FontManager | null = null;
  private fonts: Map<string, FontInfo> = new Map();
  private loadedFonts: Set<string> = new Set();
  private isOfflineMode: boolean = false;
  private isInitialized: boolean = false;
  private googleFontsLoaded: boolean = false;
  private googleFontsLoading: Promise<string[]> | null = null;

  private constructor() {
    this.detectOfflineMode();
    this.initializeSystemFonts();
  }

  /**
   * Obtient l'instance unique du FontManager (Singleton strict)
   */
  public static getInstance(): FontManager {
    if (!FontManager.instance) {
      FontManager.instance = new FontManager();
    }
    return FontManager.instance;
  }

  /**
   * Réinitialise l'instance (uniquement pour les tests)
   */
  public static resetInstance(): void {
    FontManager.instance = null;
  }
  /**
   * Initialise le FontManager (sans charger toutes les polices);
   */
  public initialize(): void {
    if (this.isInitialized) {
      logger.warn('FontManager d\u00e9j\u00e0 initialis\u00e9');
      return;
    }
    // Ne plus charger toutes les polices au démarrage;
    // Elles seront chargées à la demande via loadFontOnDemand;
    this.isInitialized = true;
    logger.info('\ud83d\udd24 FontManager initialis\u00e9 (lazy loading activé)');
  }

  /**
   * Charge une police si elle n'est pas déjà chargée;
   */
  public async ensureFontLoaded(fontFamily: string): Promise<boolean> {
    if (this.isFontLoaded(fontFamily)) {
      return true;
    }
    return await this.loadFontOnDemand(fontFamily);
  }
  /**
   * D\u00e9tecte le mode hors ligne;
   */
  private detectOfflineMode(): void {
    try {
      this.isOfflineMode =
        !navigator.onLine || localStorage.getItem('snapbooth_offline') === 'true';
    } catch (error) {
      logger.error('Erreur lors de la détection du mode hors ligne:', error);
      this.isOfflineMode = false;
    }
  }
  /**
   * Initialise les polices syst\u00e8me;
   */
  private initializeSystemFonts(): void {
    const systemFonts = [
      { family: 'Arial', category: 'Sans-Serif', variants: ['normal', 'bold', 'italic'] },
      { family: 'Helvetica', category: 'Sans-Serif', variants: ['normal', 'bold', 'italic'] },
      { family: 'Times New Roman', category: 'Serif', variants: ['normal', 'bold', 'italic'] },
      { family: 'Courier New', category: 'Monospace', variants: ['normal', 'bold', 'italic'] },
      { family: 'Georgia', category: 'Serif', variants: ['normal', 'bold', 'italic'] },
      { family: 'Verdana', category: 'Sans-Serif', variants: ['normal', 'bold', 'italic'] },
    ];
    systemFonts.forEach(font => {
      this.fonts.set(font.family, {
        family: font.family,
        category: font.category,
        variants: font.variants,
        loaded: true,
        source: 'system',
      });
    });
  }
  /**
   * Charge toutes les polices disponibles;
   */
  public loadFonts(): Promise<void> {
    return new Promise((resolve, _reject) => {
      if (this.isOfflineMode) {
        logger.info('\ud83d\udcf1 Mode hors ligne - Utilisation des polices syst\u00e8me');
        resolve();
        return;
      }
      // Attendre que WebFont soit disponible;
      this.waitForWebFont()
        .then(() => {
          logger.info('\ud83d\udd24 Chargement des polices Google...');
          resolve();
        })
        .catch(_error => {
          logger.warn('\u26a0\ufe0f WebFont non disponible, utilisation des polices syst\u00e8me');
          resolve();
        });
    });
  }
  /**
   * Attend que WebFont soit disponible;
   * CORRIGÉ: Ajout d'un timeout de sécurité de 2 secondes pour éviter que l'application freeze
   */
  private waitForWebFont(): Promise<void> {
    return new Promise((resolve, _reject) => {
      // Timeout de sécurité de 2 secondes
      const timeout = setTimeout(() => {
        logger.warn('⏱️ WebFont timeout - Passage en mode offline');
        resolve(); // On continue même sans les fonts web
      }, 2000);

      let attempts = 0;
      const maxAttempts = 50;
      const interval = 100;
      const checkWebFont = () => {
        if (window.WebFont) {
          clearTimeout(timeout);
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkWebFont, interval);
        } else {
          clearTimeout(timeout);
          logger.warn('⚠️ WebFont non disponible après tous les essais - Passage en mode offline');
          resolve(); // On continue même sans les fonts web (ne pas rejeter pour éviter de bloquer l'app)
        }
      };
      checkWebFont();
    });
  }
  /**
   * Charge une police \u00e0 la demande;
   * CORRIGÉ: Ajout d'un timeout de sécurité et forçage du redraw après chargement
   */
  public loadFontOnDemand(fontFamily: string): Promise<boolean> {
    return new Promise(resolve => {
      if (this.isOfflineMode) {
        logger.info(`\ud83d\udcf1 Mode hors ligne - Police ${fontFamily} non charg\u00e9e`);
        resolve(false);
        return;
      }

      // Timeout de sécurité : si WebFont ne répond pas en 2 secondes, basculer sur police système
      const safetyTimeout = setTimeout(() => {
        logger.warn(
          `\u26a0\ufe0f Timeout de sécurité - Police ${fontFamily} non chargée, utilisation de la police système`
        );
        resolve(false);
      }, 2000);

      this.waitForWebFont()
        .then(() => {
          const WebFont = window.WebFont;
          if (!WebFont) {
            clearTimeout(safetyTimeout);
            logger.warn(`\u26a0\ufe0f WebFont non disponible pour ${fontFamily}`);
            resolve(false);
            return;
          }

          WebFont.load({
            google: {
              families: [fontFamily],
            },
            fontactive: (familyName: string) => {
              clearTimeout(safetyTimeout);
              logger.info(`\u2705 Police charg\u00e9e: ${familyName}`);
              this.loadedFonts.add(familyName);

              // CORRIGÉ: Émettre l'événement avec le nom de la police pour que CanvasManager redessine
              this.emitFontLoadedEvent(familyName);

              // CORRIGÉ: Forcer le redraw de tous les layers Konva pour afficher la nouvelle police
              this.forceKonvaRedraw();

              resolve(true);
            },
            fontinactive: (familyName: string) => {
              clearTimeout(safetyTimeout);
              logger.warn(`\u274c \u00c9chec du chargement: ${familyName}`);
              // PHASE 3.4: Notifier l'utilisateur avec un toast
              const errorEvent = new CustomEvent('app-error', {
                detail: {
                  type: 'warning',
                  message: `Police ${familyName} non disponible, utilisation de la police système`,
                  severity: 'medium',
                },
                bubbles: true,
              });
              document.dispatchEvent(errorEvent);
              resolve(false);
            },
            timeout: 5000,
          });
        })
        .catch(() => {
          clearTimeout(safetyTimeout);
          logger.warn(`\u26a0\ufe0f WebFont non disponible pour ${fontFamily}`);
          resolve(false);
        });
    });
  }

  /**
   * Force le redraw de tous les layers Konva pour afficher les nouvelles polices
   * CORRIGÉ: Nécessaire car Konva ne redessine pas automatiquement après le chargement d'une police
   */
  private forceKonvaRedraw(): void {
    try {
      // Chercher tous les stages Konva dans le DOM
      const konvaContainers = document.querySelectorAll('[data-konva-stage]');
      konvaContainers.forEach(container => {
        interface ContainerWithKonvaStage extends HTMLElement {
          __konvaStage?: Konva.Stage;
        }
        const stage = (container as ContainerWithKonvaStage).__konvaStage;
        if (stage && typeof stage.batchDraw === 'function') {
          // Redessiner tous les layers
          stage.children.forEach((layer: Konva.Layer) => {
            if (layer && typeof layer.batchDraw === 'function') {
              layer.batchDraw();
            }
          });
        }
      });
    } catch (error) {
      logger.warn('⚠️ Erreur lors du forçage du redraw Konva:', error);
    }
  }
  /**
   * Émet un événement personnalisé pour notifier le chargement d'une police
   * CORRIGÉ: Permet à CanvasManager de redessiner après le chargement avec le nom de la police
   */
  private emitFontLoadedEvent(fontFamily: string): void {
    // Émettre un événement personnalisé que CanvasManager peut écouter
    const event = new CustomEvent('fontLoaded', {
      detail: { fontFamily },
      bubbles: true,
    });
    document.dispatchEvent(event);

    // Émettre aussi l'événement générique pour compatibilité
    document.dispatchEvent(
      new CustomEvent('font-loaded', {
        detail: { fontFamily, timestamp: Date.now() },
        bubbles: true,
      })
    );
  }

  /**
   * Charge la liste des Google Fonts depuis l'API
   */
  private async fetchGoogleFontsList(): Promise<string[]> {
    // Si déjà en cours de chargement, retourner la même promesse
    if (this.googleFontsLoading) {
      return this.googleFontsLoading;
    }

    // Si déjà chargé, retourner la liste depuis le cache
    if (this.googleFontsLoaded) {
      return Array.from(this.fonts.keys()).filter(family => {
        const font = this.fonts.get(family);
        return font && font.source === 'google';
      });
    }

    const apiKey = APP_CONFIG.GOOGLE_FONTS_API_KEY;
    if (!apiKey) {
      logger.warn('⚠️ Clé API Google Fonts non configurée, utilisation des polices par défaut');
      return this.getDefaultFonts();
    }

    this.googleFontsLoading = (async () => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&sort=popularity`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        interface GoogleFontItem {
          family: string;
          [key: string]: unknown;
        }
        const fontFamilies: string[] = (data.items || []).map(
          (font: GoogleFontItem) => font.family
        );

        // Ajouter les polices Google à la map
        fontFamilies.forEach(family => {
          if (!this.fonts.has(family)) {
            this.fonts.set(family, {
              family,
              category: this.categorizeFont(family),
              variants: ['normal', 'bold', 'italic'],
              loaded: false,
              source: 'google',
            });
          }
        });

        this.googleFontsLoaded = true;
        logger.info(`✅ ${fontFamilies.length} Google Fonts chargées depuis l'API`);
        return fontFamilies;
      } catch (error) {
        logger.error('Erreur lors du chargement des Google Fonts:', error);
        this.googleFontsLoading = null;
        return this.getDefaultFonts();
      }
    })();

    return this.googleFontsLoading;
  }

  /**
   * Retourne la liste des polices par défaut (fallback)
   */
  private getDefaultFonts(): string[] {
    return [
      'Roboto',
      'Open Sans',
      'Lato',
      'Montserrat',
      'Oswald',
      'Raleway',
      'Playfair Display',
      'Merriweather',
      'PT Sans',
      'Source Sans Pro',
    ];
  }

  /**
   * Catégorise une police selon son nom
   */
  private categorizeFont(fontFamily: string): string {
    const lower = fontFamily.toLowerCase();
    if (lower.includes('serif') || lower.includes('slab')) {
      return 'Serif';
    }
    if (lower.includes('mono') || lower.includes('code')) {
      return 'Monospace';
    }
    if (lower.includes('script') || lower.includes('handwriting') || lower.includes('cursive')) {
      return 'Handwriting';
    }
    if (lower.includes('display') || lower.includes('decorative')) {
      return 'Display';
    }
    return 'Sans-Serif';
  }

  /**
   * Obtient toutes les polices disponibles (charge les Google Fonts si nécessaire)
   */
  public async getAllFonts(): Promise<FontInfo[]> {
    // Charger les Google Fonts si pas encore fait
    if (!this.googleFontsLoaded && !this.googleFontsLoading) {
      await this.fetchGoogleFontsList();
    }
    return Array.from(this.fonts.values());
  }

  /**
   * Obtient toutes les polices disponibles (version synchrone, retourne seulement celles déjà chargées)
   */
  public getAllFontsSync(): FontInfo[] {
    return Array.from(this.fonts.values());
  }
  /**
   * Obtient les polices par catégorie (version synchrone)
   */
  public getFontsByCategory(): Map<string, string[]> {
    const categories = new Map<string, string[]>();
    this.fonts.forEach(font => {
      if (!categories.has(font.category)) {
        categories.set(font.category, []);
      }
      const categoryArray = categories.get(font.category);
      if (categoryArray) {
        categoryArray.push(font.family);
      }
    });
    return categories;
  }

  /**
   * Obtient les polices par catégorie (version asynchrone avec chargement Google Fonts)
   */
  public async getFontsByCategoryAsync(): Promise<Map<string, string[]>> {
    // Charger les Google Fonts si pas encore fait
    if (!this.googleFontsLoaded && !this.googleFontsLoading) {
      await this.fetchGoogleFontsList();
    }
    return this.getFontsByCategory();
  }
  /**
   * Recherche des polices (version synchrone)
   */
  public searchFonts(query: string): FontInfo[] {
    const lowerQuery = query.toLowerCase();
    return Array.from(this.fonts.values()).filter(font =>
      font.family.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Recherche des polices (version asynchrone avec chargement Google Fonts)
   */
  public async searchFontsAsync(query: string): Promise<FontInfo[]> {
    // Charger les Google Fonts si pas encore fait
    if (!this.googleFontsLoaded && !this.googleFontsLoading) {
      await this.fetchGoogleFontsList();
    }
    return this.searchFonts(query);
  }
  /**
   * Obtient la cat\u00e9gorie d'une police;
   */
  public getFontCategory(fontFamily: string): string | null {
    const font = this.fonts.get(fontFamily);
    return font ? font.category : null;
  }
  /**
   * V\u00e9rifie si une police est charg\u00e9e;
   */
  public isFontLoaded(fontFamily: string): boolean {
    return this.loadedFonts.has(fontFamily) || this.fonts.has(fontFamily);
  }
  /**
   * V\u00e9rifie si une police existe;
   */
  public fontExists(fontFamily: string): boolean {
    return this.fonts.has(fontFamily);
  }
  /**
   * Vérifie le mode hors ligne;
   */
  public getOfflineMode(): boolean {
    return this.isOfflineMode;
  }
  /**
   * Préchage une catégorie de polices
   */
  public async preloadCategory(category: string): Promise<void> {
    // Charger les Google Fonts si pas encore fait
    if (!this.googleFontsLoaded && !this.googleFontsLoading) {
      await this.fetchGoogleFontsList();
    }
    const categoryFonts = this.getFontsByCategory().get(category) || [];
    const loadPromises = categoryFonts.map(font => this.loadFontOnDemand(font));
    await Promise.all(loadPromises);
    logger.info(`\ud83d\udd24 Cat\u00e9gorie ${category} pr\u00e9charg\u00e9e`);
  }
  /**
   * Obtient les informations d'une police;
   */
  public getFontInfo(fontFamily: string): FontInfo | null {
    return this.fonts.get(fontFamily) || null;
  }
  /**
   * Ajoute une police personnalis\u00e9e;
   */
  public addCustomFont(fontInfo: FontInfo): void {
    this.fonts.set(fontInfo.family, fontInfo);
    logger.info(`\u2795 Police personnalis\u00e9e ajout\u00e9e: ${fontInfo.family}`);
  }
  /**
   * Supprime une police;
   */
  public removeFont(fontFamily: string): boolean {
    if (this.fonts.has(fontFamily)) {
      this.fonts.delete(fontFamily);
      this.loadedFonts.delete(fontFamily);
      logger.info(`\u2796 Police supprim\u00e9e: ${fontFamily}`);
      return true;
    }
    return false;
  }
  /**
   * Vide le cache des polices;
   */
  public clearFontCache(): void {
    this.loadedFonts.clear();
    logger.info('\ud83e\uddf9 Cache des polices vid\u00e9');
  }
  /**
   * Obtient les statistiques des polices;
   */
  public getFontStatistics(): {
    totalFonts: number;
    loadedFonts: number;
    categories: number;
    offlineMode: boolean;
  } {
    const categories = new Set(Array.from(this.fonts.values()).map(f => f.category));
    return {
      totalFonts: this.fonts.size,
      loadedFonts: this.loadedFonts.size,
      categories: categories.size,
      offlineMode: this.isOfflineMode,
    };
  }
  /**
   * V\u00e9rifie si le FontManager est initialis\u00e9;
   */
  public isFontManagerInitialized(): boolean {
    return this.isInitialized;
  }
  /**
   * Nettoyage des ressources;
   */
  public destroy(): void {
    this.clearFontCache();
    this.fonts.clear();
    this.isInitialized = false;
    logger.info('\ud83e\uddf9 FontManager d\u00e9truit');
  }
}
